/**
 * Manufacturer Products Page
 * ==========================
 * Display products for a specific manufacturer.
 */

import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Package, ChevronLeft, ChevronRight, Plus, Factory } from 'lucide-react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import Button from '@/components/Button'
import { useProducts } from '@/hooks/useProducts'
import { useManufacturer } from '@/hooks/useManufacturers'
import { getProductThumbnailUrl } from '@/utils/images'
import type { ProductSearchParams } from '@/types'

export default function ManufacturerProducts() {
  const { manufacturerId } = useParams<{ manufacturerId: string }>()
  const navigate = useNavigate()
  const manufacturerIdNum = manufacturerId ? parseInt(manufacturerId, 10) : null

  const [searchParams, setSearchParams] = useState<ProductSearchParams>({
    page: 1,
    limit: 20,
    manufacturer_id: manufacturerIdNum,
  })

  // Fetch manufacturer details
  const { data: manufacturerData, isLoading: manufacturerLoading } = useManufacturer(manufacturerIdNum ?? 0)
  const manufacturer = manufacturerData?.data

  // Fetch products for this manufacturer
  const { data, isLoading, error } = useProducts(searchParams)
  const products = data?.data ?? []
  const meta = data?.meta

  const handlePageChange = (newPage: number) => {
    setSearchParams((prev) => ({ ...prev, page: newPage }))
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">Error loading products</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/manufacturers')}
            title="Back to manufacturers"
          >
            <ArrowLeft size={20} />
          </Button>
          <div className="flex items-center gap-4">
            {/* Manufacturer logo */}
            {manufacturer?.logo_url ? (
              <img
                src={manufacturer.logo_url}
                alt={manufacturer.name}
                className="h-12 w-12 object-contain rounded"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none'
                }}
              />
            ) : (
              <div className="h-12 w-12 flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700">
                <Factory className="text-gray-400" size={24} />
              </div>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                {manufacturerLoading ? 'Loading...' : manufacturer?.name ?? 'Manufacturer Products'}
              </h1>
              <p className="text-gray-500 dark:text-gray-400">
                {meta && `${meta.total} product${meta.total !== 1 ? 's' : ''}`}
              </p>
            </div>
          </div>
        </div>
        <Link to="/products/new">
          <Button icon={<Plus size={16} />}>Add Product</Button>
        </Link>
      </div>

      {/* Info banner */}
      <Card>
        <CardContent>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing all products from <strong>{manufacturer?.name}</strong>.
          </p>
        </CardContent>
      </Card>

      {/* Products list */}
      <Card>
        <CardHeader title="Products" />
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ID / SKU
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Qty
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    Loading...
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <Package className="mx-auto text-gray-300 dark:text-gray-600 mb-4" size={48} />
                    <p className="text-gray-500 dark:text-gray-400">No products from this manufacturer</p>
                    <Link
                      to="/products/new"
                      className="text-primary-600 dark:text-primary-400 hover:underline mt-2 inline-block"
                    >
                      Add a product
                    </Link>
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/products/${product.product_id}`}
                        className="flex items-center gap-3"
                      >
                        {getProductThumbnailUrl(product) ? (
                          <img
                            src={getProductThumbnailUrl(product)!}
                            alt={product.name}
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                            <Package className="text-gray-400" size={20} />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {product.name}
                          </p>
                          {product.description && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {product.description}
                            </p>
                          )}
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm font-mono text-gray-900 dark:text-white">
                        {product.product_id}
                      </p>
                      {product.sku && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{product.sku}</p>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {product.category?.name ?? '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {product.location?.name ?? '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          product.quantity === 0
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
                            : product.quantity < 5
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-400'
                              : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                        }`}
                      >
                        {product.quantity}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {meta && meta.total_pages > 1 && (
          <div className="px-6 py-4 border-t dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {(meta.page - 1) * meta.limit + 1} to{' '}
              {Math.min(meta.page * meta.limit, meta.total)} of {meta.total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_prev}
                onClick={() => handlePageChange(meta.page - 1)}
              >
                <ChevronLeft size={16} />
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!meta.has_next}
                onClick={() => handlePageChange(meta.page + 1)}
              >
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  )
}
